/**
 * OptionsButton — reusable button that shows a popup menu of options on click.
 *
 * Displays the active option's icon as the button face, plus optional children.
 * Clicking anywhere on the component opens the menu above it.
 *
 * Supports two sections:
 *   - options: mutually exclusive radio-style items
 *   - toggles: independent checkbox-style items (below a divider)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from './Tooltip';

export interface Option<T extends string = string> {
  value: T;
  label: string;
  color: string;
  icon: React.ReactNode;
  tooltip?: string;
}

export interface Toggle {
  key: string;
  label: string;
  color: string;
  icon: React.ReactNode;
  checked: boolean;
  tooltip?: string;
}

interface Props<T extends string = string> {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  toggles?: Toggle[];
  onToggle?: (key: string, checked: boolean) => void;
  title?: string;
  children?: React.ReactNode;
}

export function OptionsButton<T extends string = string>({ options, value, onChange, toggles, onToggle, title = 'Options', children }: Props<T>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ bottom: number; left: number }>({ bottom: 0, left: 0 });

  const activeOption = options.find(o => o.value === value) ?? options[0];

  const handleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (open) { setOpen(false); return; }
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
    }
    setOpen(true);
  }, [open]);

  // Close on click-outside or Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = useCallback((val: T) => {
    setOpen(false);
    if (val !== value) onChange(val);
  }, [value, onChange]);

  const handleToggle = useCallback((key: string, checked: boolean) => {
    onToggle?.(key, !checked);
  }, [onToggle]);

  return (
    <>
      <div
        ref={wrapperRef}
        className="nodrag nopan"
        onClick={handleOpen}
        title={title}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <div style={{ padding: '0 2px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
          {activeOption.icon}
        </div>
        {children}
      </div>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            bottom: menuPos.bottom,
            left: menuPos.left,
            background: '#283444',
            border: '1px solid #3f4f5f',
            borderRadius: 6,
            padding: '4px 0',
            zIndex: 10000,
            minWidth: 120,
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          {options.map(opt => (
            <Tooltip key={opt.value} text={opt.tooltip} placement="right">
              <button
                className="nodrag nopan"
                onClick={(e) => { e.stopPropagation(); handleSelect(opt.value); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '5px 10px',
                  background: value === opt.value ? '#3a4a5a' : 'transparent',
                  border: 'none',
                  color: value === opt.value ? 'white' : '#9ca3af',
                  fontSize: 11,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={e => { if (value !== opt.value) (e.currentTarget.style.background = '#334155'); }}
                onMouseLeave={e => { if (value !== opt.value) (e.currentTarget.style.background = 'transparent'); }}
              >
                {opt.icon}
                {opt.label}
              </button>
            </Tooltip>
          ))}

          {toggles && toggles.length > 0 && (
            <>
              <div style={{ borderTop: '1px solid #3f4f5f', margin: '4px 0' }} />
              {toggles.map(t => (
                <Tooltip key={t.key} text={t.tooltip} placement="right">
                  <button
                    className="nodrag nopan"
                    onClick={(e) => { e.stopPropagation(); handleToggle(t.key, t.checked); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '5px 10px 5px 32px',
                      background: 'transparent',
                      border: 'none',
                      color: t.checked ? 'white' : '#9ca3af',
                      fontSize: 11,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#334155'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {t.label}
                    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>{t.icon}</span>
                  </button>
                </Tooltip>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
